import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import { Image } from '@/components/Image';
import { Text } from '@/components/Text';
import { Event } from '@/types/event';
import { formatDateTime } from '@/utils/formatDateTime';
import { motion } from 'framer-motion';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { BsCalendar, BsCalendarEvent, BsEye, BsPeople } from 'react-icons/bs';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { FaBell } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

interface EventCardProps {
  event: Event;
}

export const EventCard = ({ event }: EventCardProps) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  return (
    <motion.div
      layout
      onClick={() => navigate(`/${i18n.language}/dashboard/events/${event.id}`)}
      className="group relative h-full cursor-pointer transition-transform"
    >
      <Card className="flex h-full flex-col overflow-hidden !p-0">
        <div className="relative overflow-hidden rounded-t-lg">
          <div className="aspect-video w-full overflow-hidden bg-gray-100">
            {event.cover ? (
              <Image
                src={event.cover}
                alt={event.name}
                className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                <BsCalendar className="size-12 text-primary/50" />
              </div>
            )}
          </div>
          <div className="absolute right-2 top-2 flex flex-col items-end gap-2">
            <Badge
              label={t(`common:status.${event.status}`)}
              color={
                event.status === 'future'
                  ? 'blue'
                  : event.status === 'past'
                    ? 'red'
                    : 'green'
              }
            />
            {event.user_is_subscribed && (
              <Badge label={t('common:subscribed')} color="green" />
            )}
            {event.meeting_requests > 0 && (
              <div className="relative">
                <div className="flex items-center gap-1 rounded-full bg-orange-500 px-2 py-1 text-xs font-medium text-white shadow-lg">
                  <FaBell className="size-3" />
                  <span>{event.meeting_requests}</span>
                </div>
              </div>
            )}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2">
              <BsEye className="size-5 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">
                {t('common:view')}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 p-4">
          <div>
            <Text className="line-clamp-1 text-lg font-semibold">
              {event.name}
            </Text>
            <Text className="flex items-center gap-1 text-sm text-gray-500">
              <BsCalendarEvent className="mr-1 size-3.5" />
              {formatDateTime(event.date)}
              {event.end_date && (
                <>
                  <span className="mx-1">→</span>
                  {formatDateTime(event.end_date)}
                </>
              )}
            </Text>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <BsPeople className="size-4" />
                {event.show_capacity ? (
                  <>
                    <span>{event.total_users}</span>
                    {event.maximum_participants && (
                      <>
                        <span className="text-gray-400">/</span>
                        <span>{event.maximum_participants}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span>{event.total_users}</span>
                )}
              </div>
              {event.location && (
                <div className="flex items-center gap-1">
                  <FaMapMarkerAlt className="size-4" />
                  <span className="line-clamp-1">{event.location}</span>
                </div>
              )}
            </div>
          </div>
          {event.description && (
            <Text className="line-clamp-3 text-sm text-gray-600">
              {event.description}
            </Text>
          )}
        </div>
      </Card>
    </motion.div>
  );
};
